import { useMemo, useState } from "react";
import { format, subDays } from "date-fns";
import type { DateRange } from "react-day-picker";
import { CalendarDays } from "lucide-react";
import { toast } from "sonner";
import OverviewStats from "@/components/overview/OverviewStats";
import RecentActivity from "@/components/overview/RecentActivity";
import RequiresAttention from "@/components/overview/RequiresAttention";
import DateRangeFilter from "@/components/common/DateRangeFilter";
import ExportButton from "@/components/common/ExportButton";
import NewDealLauncher from "@/components/deals/new-deal/NewDealLauncher";
import OverviewSkeleton from "@/components/overview/skeletons/OverviewSkeleton";
import { exportOverviewReport } from "@/utils/overview.utils";
import {
  useGetOverviewActivity,
  useGetOverviewStats,
  useGetRequiresAttention,
} from "@/hooks/useOverview";
import { useGetDeals } from "@/hooks/useDeals";
import { getOverviewExportService } from "@/services/overview.service";

/** The cards show the top handful; the endpoints page the rest. */
const CARD_LIMIT = 6;

export default function Overview() {
  // Defaults to the last 30 days, matching the range control's own preset.
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => ({
    from: subDays(new Date(), 29),
    to: new Date(),
  }));

  // The range is sent to the server as plain ISO days — every Overview endpoint
  // takes the same `from`/`to`, so one object drives all four.
  const range = useMemo(() => ({
    from: dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined,
    to: dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined,
  }), [dateRange]);

  const { data: stats, isLoading: statsLoading } = useGetOverviewStats(range);
  // Deliberately NOT given the date range. "What needs attention" is a
  // statement about the pipeline right now, so filtering it by when the deal
  // was *created* hid any deal older than the range from a card whose whole job
  // is surfacing problems — a deal opened in June that goes blocked today is
  // exactly the one an agent must not miss.
  const { data: attention, isLoading: attentionLoading } = useGetRequiresAttention({
    limit: CARD_LIMIT,
  });
  // The pipeline behind that card. Without this the dashboard had nothing to
  // say once nothing was flagged: active deals existed on the board but the
  // Overview showed "Everything is on track" over an empty panel, which read as
  // the deals having failed to save.
  const { data: dealsData, isLoading: dealsLoading } = useGetDeals({ limit: CARD_LIMIT });
  const { data: activity, isLoading: activityLoading } = useGetOverviewActivity({
    ...range, limit: CARD_LIMIT,
  });

  const loading = statsLoading || attentionLoading || activityLoading || dealsLoading;

  const today = new Intl.DateTimeFormat("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  }).format(new Date());

  // Fetched on click rather than kept warm: the export is the full matching
  // set, not the six rows the cards happen to show.
  const exportReport = async () => {
    const { rows } = await getOverviewExportService(range);
    if (!rows.length) {
      toast.info("There are no deals to export");
      return;
    }
    exportOverviewReport(rows);
  };

  return (
    <div className="mx-auto flex w-full max-w-[1680px] flex-col gap-5 font-dashboard" data-testid="overview-page">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 text-dash-faint">
          <CalendarDays className="size-5" />
          <p className="text-sm">{today}</p>
        </div>
        <div className="grid w-full grid-cols-2 items-center gap-3 sm:flex sm:w-auto sm:flex-wrap">
          <DateRangeFilter value={dateRange} onChange={setDateRange} showPreset className="col-span-2 w-full sm:w-auto" testId="overview-date-range" />
          <ExportButton
            onExport={exportReport}
            label="Export report"
            className="h-11 w-full !rounded-xl sm:w-auto"
            testId="overview-export"
          />
          <NewDealLauncher className="h-11 w-full !rounded-xl sm:w-auto" testId="overview-new-deal-btn" />
        </div>
      </header>

      {loading ? (
        <OverviewSkeleton />
      ) : (
        <>
          <OverviewStats stats={stats} />
          <section className="grid items-stretch gap-5 xl:grid-cols-[1.35fr_1fr]" aria-label="Dashboard highlights">
            <RequiresAttention
              items={attention?.items ?? []}
              total={attention?.pagination.total ?? 0}
              activeDeals={dealsData?.deals ?? []}
              activeTotal={dealsData?.pagination.total ?? 0}
            />
            <RecentActivity items={activity?.items ?? []} />
          </section>
        </>
      )}
    </div>
  );
}
